import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchStaffCommentList, fetchCommentTabs, type CommentItem as ApiCommentItem, type CommentTabItem } from "@/api/guide";
import { stripHtml } from "@/utils/format";

/** 主题色（与 guide-detail-product 保持一致） */
const THEME = {
  primary: "#FF6B35",
  primaryLight: "#FFF0EB",
};

/** 评价类型 */
type CommentType = "all" | "good" | "medium" | "bad" | "hasImage";

/** Tab 配置：key -> service_score 映射 */
const TAB_MAP: Record<CommentType, number> = {
  all: 0,
  good: 5,
  medium: 3,
  bad: 1,
  hasImage: 4,
};

const TABS: { key: CommentType; label: string; scoreValue: number }[] = [
  { key: "all", label: "全部", scoreValue: 0 },
  { key: "good", label: "好评", scoreValue: 5 },
  { key: "medium", label: "中评", scoreValue: 3 },
  { key: "bad", label: "差评", scoreValue: 1 },
  { key: "hasImage", label: "有图", scoreValue: 4 },
];

export default function GuideCommentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const staffId = (location.state as { staffId?: number })?.staffId || (location.state as { staff_id?: number })?.staff_id || 0;

  // 筛选状态
  const [activeTab, setActiveTab] = useState<CommentType>("all");
  const [comments, setComments] = useState<ApiCommentItem[]>([]);
  const [loading, setLoading] = useState(true);

  /** 各 Tab 数量 */
  const [tabCount, setTabCount] = useState<Record<string, number>>({
    all: 0,
    good: 0,
    medium: 0,
    bad: 0,
    hasImage: 0,
  });

  /** 获取评论Tabs数量 */
  const fetchTabs = useCallback(async () => {
    if (!staffId) return;
    try {
      const tabs = await fetchCommentTabs(staffId);
      // 将API返回的 value -> count 映射为前端 Tab 计数
      const counts: Record<string, number> = {};
      tabs.forEach((t) => {
        switch (t.value) {
          case 0: counts.all = t.count; break;
          case 5: counts.good = t.count; break;
          case 3: counts.medium = t.count; break;
          case 1: counts.bad = t.count; break;
          case 4: counts.hasImage = t.count; break;
          default: break;
        }
      });
      setTabCount((prev) => ({ ...prev, ...counts }));
    } catch (e) {
      console.error("[guide-comment] 获取tabs失败:", e);
    }
  }, [staffId]);

  /** 获取评价列表 */
  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const scoreValue = TAB_MAP[activeTab];
      const res = await fetchStaffCommentList({
        service_staff_id: staffId,
        page: 1,
        limit: 20,
        service_score: scoreValue,
      });
      setComments(res.list);
    } catch (e) {
      console.error("[guide-comment] 获取评论列表失败:", e);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [staffId, activeTab]);

  useEffect(() => {
    fetchTabs();
  }, [fetchTabs]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#1A1D26", fontFamily: "'Inter','SF Pro Text',sans-serif" }}>
      {/* ========== 顶部导航栏 ========== */}
      <div className="sticky top-0 z-50 px-4 py-3 flex items-center gap-3" style={{ background: "#1A1D26" }}>
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer -ml-1"
          style={{ background: "rgba(255,255,255,0.08)" }}
        >
          <i className="ri-arrow-left-line text-white" style={{ fontSize: 18 }} />
        </button>
        <h1 className="text-base font-semibold text-white flex-1 text-center pr-10">评价列表</h1>
      </div>

      {/* ========== Tab 筛选栏 ========== */}
      <div className="px-4 pb-2 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="flex items-center gap-5">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="relative pb-2.5 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
                style={{
                  color: isActive ? THEME.primary : "rgba(255,255,255,0.45)",
                }}
              >
                {tab.label}({tabCount[tab.key] ?? 0})
                {/* 活动下划线 */}
                {isActive && (
                  <span
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full"
                    style={{ width: 20, height: 3, background: THEME.primary }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ========== 内容区 ========== */}
      <div className="flex-1">
        {/* 加载状态 */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-white/20 border-t-[#FF6B35] rounded-full animate-spin" />
          </div>
        )}

        {/* 空状态 */}
        {!loading && comments.length === 0 && (
          <div className="flex flex-col items-center pt-16 px-8">
            {/* 插图区域 */}
            <div className="relative w-48 h-36 mb-6">
              <svg viewBox="0 0 200 150" className="w-full h-full">
                <ellipse cx="100" cy="130" rx="80" ry="15" fill="#F5DEB3" opacity="0.7" />
                <polygon points="110,60 140,105 80,105" fill="#E0E7EF" opacity="0.6" />
                <polygon points="125,70 155,115 95,115" fill="#CDD9E3" opacity="0.5" />
                <rect x="65" y="72" width="70" height="48" rx="3" fill="#FFF" stroke="#E5E5E5" strokeWidth="1" />
                <rect x="69" y="76" width="62" height="40" rx="1" fill="#2D5016" opacity="0.7" />
                <line x1="35" y1="120" x2="52" y2="68" stroke="#8B6914" strokeWidth="3" strokeLinecap="round" />
                <ellipse cx="52" cy="58" rx="22" ry="8" fill="#228B22" transform="rotate(-30 52 58)" />
                <ellipse cx="55" cy="54" rx="20" ry="7" fill="#2E8B2E" transform="rotate(15 55 54)" />
                <ellipse cx="44" cy="62" rx="18" ry="6" fill="#32CD32" transform="rotate(-65 44 62)" />
                <circle cx="56" cy="66" r="3" fill="#654321" />
                <circle cx="48" cy="68" r="2.5" fill="#5D4037" />
              </svg>
            </div>
            <span className="text-sm" style={{ color: "rgba(255,255,255,0.35)" }}>这里空空如也</span>
          </div>
        )}

        {/* 评价列表 */}
        {!loading && comments.length > 0 && (
          <div className="divide-y" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
            {comments.map((item) => (
              <CommentCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** 单条评价卡片 */
function CommentCard({ item }: { item: ApiCommentItem }) {
  const scoreLabels = ["", "差评", "较差", "一般", "满意", "非常满意"];

  return (
    <div className="px-4 py-4">
      {/* 头部：头像 + 昵称 + 时间 + 评分 */}
      <div className="flex items-start gap-3">
        <img
          src={item.avatar}
          alt={item.nickname}
          className="w-9 h-9 rounded-full object-cover flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-white truncate">{item.nickname}</span>
            <span className="text-xs flex-shrink-0 ml-2" style={{ color: "rgba(255,255,255,0.3)" }}>
              {item.createTime}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {/* 星级 */}
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((i) => (
                <i
                  key={i}
                  className={i <= item.score ? "ri-star-fill" : "ri-star-line"}
                  style={{ fontSize: 12, color: i <= item.score ? "#F59E0B" : "#374151" }}
                />
              ))}
            </div>
            <span className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>{scoreLabels[item.score] || ""}</span>
          </div>
        </div>
      </div>

      {/* 评价内容 */}
      {item.content && (
        <p className="mt-2.5 text-xs leading-relaxed pl-12" style={{ color: "rgba(255,255,255,0.65)" }}>
          {item.content}
        </p>
      )}

      {/* 评价图片 */}
      {item.images.length > 0 && (
        <div className="mt-2 pl-12 flex gap-1.5 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
          {item.images.slice(0, 4).map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt={`评价图${idx + 1}`}
              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
            />
          ))}
        </div>
      )}

      {/* 商家回复 */}
      {item.reply && (
        <div className="mt-2.5 ml-12 p-3 rounded-xl" style={{ background: "rgba(255,255,255,0.04)" }}>
          <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.4)" }}>
            <span className="font-medium text-white">商家回复：</span>{item.reply}
          </p>
        </div>
      )}

      {/* 服务信息 */}
      {(item.serviceName || item.city) && (
        <div className="mt-2 pl-12 flex items-center gap-2 text-xs" style={{ color: "rgba(255,255,255,0.25)" }}>
          {item.serviceName && <span>{stripHtml(item.serviceName)}</span>}
          {item.city && item.district && <span>{stripHtml(item.city)} {stripHtml(item.district)}</span>}
        </div>
      )}
    </div>
  );
}
